import { useState, useEffect } from "react";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import "./Rooms.css";

const Rooms = () => {
  const { showToast } = useToast();
  const { isManagerOrAdmin, isWorkerOrAbove, isWorker, hasAccessToRoom, isAdmin } = useAuth();

  // Check if current user can manage tags in a specific room
  const canManageRoomTags = (room) => {
    if (isManagerOrAdmin()) return true;
    if (isWorker() && room) {
      return hasAccessToRoom(room.rid);
    }
    return false;
  };

  const [rooms, setRooms] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [totalRooms, setTotalRooms] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTagAssignModal, setShowTagAssignModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [roomForm, setRoomForm] = useState({
    roomTitle: "",
    warehouseId: ""
  });

  const [selectedTagsForBulk, setSelectedTagsForBulk] = useState([]);
  const [selectedTagsToRemove, setSelectedTagsToRemove] = useState([]);
  const [roomTags, setRoomTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [showMoveTagsModal, setShowMoveTagsModal] = useState(false);
  const [selectedMoveRoomRid, setSelectedMoveRoomRid] = useState(null);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const data = await api.getRooms({ forTagAssignment: true });
      setRooms(data.rooms || []);
      setTotalRooms(data.total || data.rooms?.length || 0);
    } catch (error) {
      showToast(error.message || "Failed to fetch rooms", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await api.getWarehouses(1, 100);
      setWarehouses(data.warehouses || []);
    } catch (error) {
      console.error("Failed to fetch warehouses:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations(1, 100);
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const data = await api.getTags(1, 10000);
      setTags(data.tags || []);
    } catch (error) {
      console.error("Failed to fetch tags:", error);
    }
  };

  const fetchRoomTags = async (roomId) => {
    setLoadingTags(true);
    try {
      const data = await api.getRoomById(roomId, 1, 1000);
      setRoomTags(data.tags || []);
    } catch (error) {
      console.error("Failed to fetch room tags:", error);
      setRoomTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const createRoom = async () => {
    if (!roomForm.roomTitle.trim()) {
      showToast("Partition title is required", 2000, true, "danger");
      return;
    }

    if (!roomForm.warehouseId) {
      showToast("Please select a warehouse", 2000, true, "danger");
      return;
    }

    try {
      await api.createRoom(roomForm);
      showToast("Partition created successfully!", 2000, true, "success");
      setShowRoomModal(false);
      resetRoomForm();
      fetchRooms();
    } catch (error) {
      showToast(error.message || "Failed to create room", 2000, true, "danger");
      console.error(error);
    }
  };

  const updateRoom = async () => {
    if (!roomForm.roomTitle.trim()) {
      showToast("Partition title is required", 2000, true, "danger");
      return;
    }

    // Build the payload — only include warehouseId when it has actually
    // changed AND the current user is allowed to change it (admin only,
    // since the API enforces the same rule and a 403 would otherwise be
    // misleading to the user).
    const payload = { roomTitle: roomForm.roomTitle };
    const currentWarehouseId =
      editingRoom?.warehouseId?._id || editingRoom?.warehouseId || "";
    if (
      isAdmin?.() &&
      roomForm.warehouseId &&
      roomForm.warehouseId !== currentWarehouseId
    ) {
      payload.warehouseId = roomForm.warehouseId;
    }

    try {
      await api.updateRoom(editingRoom.rid, payload);
      showToast("Partition updated successfully!", 2000, true, "success");
      setShowRoomModal(false);
      setEditingRoom(null);
      resetRoomForm();
      fetchRooms();
      // Refresh tags too — moving a room between warehouses cascades to
      // every tag's denormalised warehouseId/locationId.
      fetchTags();
    } catch (error) {
      showToast(error.message || "Failed to update room", 2000, true, "danger");
      console.error(error);
    }
  };

  const deleteRoom = async (deleteTags = false, moveTagsToRid = null) => {
    if (!deleteTarget) return;

    try {
      await api.deleteRoom(deleteTarget.rid, {
        deleteTags,
        moveTagsToRid,
      });
      showToast("Partition deleted successfully!", 2000, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchRooms();
    } catch (error) {
      showToast(error.message || "Failed to delete room", 2000, true, "danger");
      console.error(error);
    }
  };

  const handleMoveTagsOnRoomDelete = () => {
    if (!deleteTarget) return;
    setSelectedMoveRoomRid(null);
    setShowMoveTagsModal(true);
  };

  const confirmMoveTagsOnRoomDelete = () => {
    if (!selectedMoveRoomRid) {
      showToast("Please select a target partition", 2000, true, "danger");
      return;
    }
    deleteRoom(false, selectedMoveRoomRid);
    setShowMoveTagsModal(false);
  };

  const assignTagToRoom = async (tid) => {
    if (!selectedRoom) return;

    try {
      await api.assignTagToRoom(selectedRoom.rid, tid);
      showToast(`Tag ${tid} assigned!`, 2000, true, "success");
      setTimeout(() => {
        fetchRoomTags(selectedRoom.rid);
        fetchTags();
      }, 1200);
    } catch (error) {
      showToast(error.message || "Failed to assign tag", 2000, true, "danger");
      console.error(error);
    }
  };

  const bulkAssignTagsToRoom = async () => {
    if (!selectedRoom || selectedTagsForBulk.length === 0) {
      showToast("Please select tags to assign", 2000, true, "danger");
      return;
    }

    try {
      await api.bulkAssignTagsToRoom(selectedRoom.rid, selectedTagsForBulk);
      showToast(`${selectedTagsForBulk.length} tags assigned!`, 2000, true, "success");
      setSelectedTagsForBulk([]);
      const rid = selectedRoom.rid;
      setTimeout(() => {
        fetchRoomTags(rid);
        fetchTags();
      }, 1200);
    } catch (error) {
      showToast(error.message || "Failed to bulk assign tags", 2000, true, "danger");
      console.error(error);
    }
  };

  const bulkRemoveTagsFromRoom = async () => {
    if (!selectedRoom || selectedTagsToRemove.length === 0) return;

    try {
      const res = await api.bulkRemoveTagsFromRoom(selectedRoom.rid, selectedTagsToRemove);
      showToast(res.message || `${selectedTagsToRemove.length} tag(s) removed!`, 2000, true, "success");
      setSelectedTagsToRemove([]);
      const rid = selectedRoom.rid;
      setTimeout(() => {
        fetchRoomTags(rid);
        fetchTags();
      }, 1200);
    } catch (error) {
      showToast(error.message || "Failed to remove tags", 2000, true, "danger");
      console.error(error);
    }
  };

  const removeTagFromRoom = async (tid) => {
    if (!selectedRoom) return;

    try {
      await api.removeTagFromRoom(selectedRoom.rid, tid);
      showToast("Tag removed!", 2000, true, "success");
      const rid = selectedRoom.rid;
      setTimeout(() => {
        fetchRoomTags(rid);
        fetchTags();
      }, 1200);
    } catch (error) {
      showToast(error.message || "Failed to remove tag", 2000, true, "danger");
      console.error(error);
    }
  };

  const toggleTagSelection = (tid) => {
    setSelectedTagsForBulk(prev =>
      prev.includes(tid)
        ? prev.filter(t => t !== tid)
        : [...prev, tid]
    );
  };

  const selectAllAvailableTags = () => {
    const availableTids = getAvailableTags().map(t => t.tid);
    setSelectedTagsForBulk(availableTids);
  };

  const clearTagSelection = () => {
    setSelectedTagsForBulk([]);
  };

  const toggleRemoveSelection = (tid) => {
    setSelectedTagsToRemove(prev =>
      prev.includes(tid) ? prev.filter(t => t !== tid) : [...prev, tid]
    );
  };

  const selectAllAssignedTags = () => {
    const tids = roomTags.map(t => t.tid);
    setSelectedTagsToRemove(prev =>
      prev.length === roomTags.length ? [] : tids
    );
  };

  const clearRemoveSelection = () => {
    setSelectedTagsToRemove([]);
  };

  const closeTagAssignModal = () => {
    setShowTagAssignModal(false);
    setSelectedTagsToRemove([]);
    clearTagSelection();
  };

  const resetRoomForm = () => {
    setRoomForm({
      roomTitle: "",
      warehouseId: ""
    });
  };

  useEffect(() => {
    fetchRooms();
    fetchWarehouses();
    fetchLocations();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [currentPage]);

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.roomTitle?.toLowerCase().includes(searchTerm.toLowerCase());
    const warehouseId = room.warehouseId?._id || room.warehouseId;
    const locationId = room.locationId?._id || room.locationId;
    const matchesWarehouse = filterWarehouse === "all" || warehouseId === filterWarehouse;
    const matchesLocation = filterLocation === "all" || locationId === filterLocation;
    return matchesSearch && matchesWarehouse && matchesLocation;
  });

  const totalPages = Math.ceil(totalRooms / pageLimit);

  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return "Unassigned";
    if (typeof warehouseId === 'object' && warehouseId.name) return warehouseId.name;
    const warehouse = warehouses.find(w => w._id === warehouseId);
    return warehouse?.name || "Unknown";
  };

  const getLocationName = (room) => {
    const locationId = room.locationId?._id || room.locationId;
    if (!locationId) {
      const warehouseId = room.warehouseId?._id || room.warehouseId;
      const warehouse = warehouses.find(w => w._id === warehouseId);
      if (warehouse?.locationId) {
        const loc = locations.find(l => l._id === (warehouse.locationId._id || warehouse.locationId));
        return loc?.name || "Unknown";
      }
      return "Unassigned";
    }
    if (typeof room.locationId === 'object' && room.locationId.name) return room.locationId.name;
    const loc = locations.find(l => l._id === locationId);
    return loc?.name || "Unknown";
  };

  const getAvailableTags = () => {
    if (!selectedRoom) return tags.filter(t => t.rid === 0 || !t.rid);
    const assignedTids = roomTags.map(t => t.tid);
    return tags.filter(t => !assignedTids.includes(t.tid) && (t.rid === 0 || t.rid === selectedRoom.rid || !t.rid));
  };

  const warehouseOptions = [
    { label: "All Warehouses", value: "all" },
    ...warehouses.map(w => ({ label: w.name, value: w._id }))
  ];

  const locationOptions = [
    { label: "All Locations", value: "all" },
    ...locations.map(loc => ({ label: loc.name, value: loc._id }))
  ];

  const warehouseFormOptions = warehouses.map(w => ({
    label: `${w.name} (${getLocationNameById(w.locationId)})`,
    value: w._id
  }));

  function getLocationNameById(locationId) {
    if (!locationId) return "No Location";
    const id = typeof locationId === 'object' ? locationId._id : locationId;
    const loc = locations.find(l => l._id === id);
    return loc?.name || "Unknown";
  }

  const getRoomTagCount = (room) => {
    return tags.filter(t => t.rid === room.rid).length;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Partitions</h1>
            <p className="dashboard-subtitle">Manage partitions and their tag assignments</p>
          </div>
          <div className="dfcc gap-2">
            {isManagerOrAdmin() && (
              <Button
                value="Add Partition"
                iconLeft="fa-plus"
                onClick={() => {
                  setEditingRoom(null);
                  resetRoomForm();
                  setShowRoomModal(true);
                }}
                variant="primary"
                padding="12px 24px"
              />
            )}
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e0e7ff" }}>
              <i className="fa fa-cube" style={{ color: "#6366f1" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{totalRooms}</h3>
              <p className="stat-label">Total Partitions</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e3f2fd" }}>
              <i className="fa fa-tag" style={{ color: "#2196F3" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {tags.filter(t => t.rid && t.rid !== 0).length}
              </h3>
              <p className="stat-label">Assigned Tags</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#fff3e0" }}>
              <i className="fa fa-building" style={{ color: "#FF9800" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {new Set(rooms.map(r => r.warehouseId?._id || r.warehouseId)).size}
              </h3>
              <p className="stat-label">Warehouses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#fce4ec" }}>
              <i className="fa fa-tags" style={{ color: "#e91e63" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {tags.filter(t => !t.rid || t.rid === 0).length}
              </h3>
              <p className="stat-label">Unassigned Tags</p>
            </div>
          </div>
        </div>

        <div className="filters-section">
          <h2 className="section-title">Search & Filter Partitions</h2>
          <div className="filters-grid">
            <TextField
              placeholder="Search by partition name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconLeft="fa-search"
              variant="outlined"
            />
            <Dropdown
              options={warehouseOptions}
              value={filterWarehouse}
              onChange={setFilterWarehouse}
              placeholder="Select Warehouse"
              variant="outlined"
            />
            <Dropdown
              options={locationOptions}
              value={filterLocation}
              onChange={setFilterLocation}
              placeholder="Select Location"
              variant="outlined"
            />
            <Button
              value="Clear"
              iconLeft="fa-times"
              onClick={() => {
                setSearchTerm("");
                setFilterWarehouse("all");
                setFilterLocation("all");
              }}
              variant="outline"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="rooms-section">
          <h2 className="section-title">Partitions</h2>
          <div className="rooms-grid">
            {loading ? (
              <div className="loading-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#6366f1" }}></i>
                <p>Loading partitions...</p>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="empty-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-cube" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p>No partitions found</p>
              </div>
            ) : (
              filteredRooms.map((room) => (
                <div key={room._id} className="room-card">
                  <div className="room-header">
                    <div className="room-icon" style={{
                      background: "linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)",
                      color: "#4338CA"
                    }}>
                      <i className="fa fa-cube"></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 className="room-title">{room.roomTitle}</h3>
                      <p className="room-id">ID: {room.rid}</p>
                    </div>
                    {isManagerOrAdmin() && (
                      <div className="room-actions">
                        <button
                          className="room-action-btn edit"
                          onClick={() => {
                            setEditingRoom(room);
                            setRoomForm({
                              roomTitle: room.roomTitle,
                              warehouseId: room.warehouseId?._id || room.warehouseId || ""
                            });
                            setShowRoomModal(true);
                          }}
                          title="Edit Partition"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        <button
                          className="room-action-btn delete"
                          onClick={() => {
                            setDeleteTarget(room);
                            setShowDeleteConfirm(true);
                          }}
                          title="Delete Partition"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="room-hierarchy">
                    <div className="hierarchy-item">
                      <i className="fa fa-map-marker" style={{ color: "#4CAF50" }}></i>
                      <span>{getLocationName(room)}</span>
                    </div>
                    <div className="hierarchy-separator">
                      <i className="fa fa-chevron-right"></i>
                    </div>
                    <div className="hierarchy-item">
                      <i className="fa fa-building" style={{ color: "#FF9800" }}></i>
                      <span>{getWarehouseName(room.warehouseId)}</span>
                    </div>
                  </div>

                  <div className="room-stats">
                    <div className="room-stat">
                      <i className="fa fa-tag"></i>
                      <span>{getRoomTagCount(room)} Tags</span>
                    </div>
                  </div>

                  <Button
                    value="Manage Tags"
                    iconRight="fa-tags"
                    onClick={() => {
                      setSelectedRoom(room);
                      setSelectedTagsForBulk([]);
                      fetchRoomTags(room.rid);
                      setShowTagAssignModal(true);
                    }}
                    variant="outline"
                    width="100%"
                    padding="10px 16px"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {!loading && totalRooms > pageLimit && (
          <div className="pagination" style={{ background: "white", padding: "16px 24px", borderRadius: "16px", border: "1px solid #E2E8F0" }}>
            <Button
              value="Previous"
              iconLeft="fa-chevron-left"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              variant="outline"
              padding="8px 16px"
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <Button
              value="Next"
              iconRight="fa-chevron-right"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              variant="outline"
              padding="8px 16px"
            />
          </div>
        )}
      </div>

      {/* Create/Edit Room Modal */}
      {showRoomModal && (
        <div className="modal-overlay" onClick={() => setShowRoomModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)", color: "#4338CA" }}>
                <i className="fa fa-cube"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingRoom ? "Edit Partition" : "Add New Partition"}</span>
                <span className="modal-subtitle">
                  {editingRoom ? "Update partition information" : "Create a new partition in a warehouse"}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowRoomModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Partition Title</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Enter partition title"
                  value={roomForm.roomTitle}
                  onChange={(e) => setRoomForm({ ...roomForm, roomTitle: e.target.value })}
                  variant="outlined"
                />
              </div>

              {/* Warehouse selector — same dropdown for both create and
                  edit modes.  In edit mode, only admins are allowed to
                  change it (the API enforces the same rule), so we lock
                  the dropdown for non-admins instead of hiding it. */}
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Warehouse</span>
                  {!editingRoom && <span className="form-label-required">*</span>}
                </div>
                {editingRoom && !isAdmin?.() ? (
                  <div style={{
                    padding: "12px 16px",
                    backgroundColor: "#f1f5f9",
                    borderRadius: "8px",
                    color: "#64748b",
                    fontSize: "14px"
                  }}>
                    <i className="fa fa-lock" style={{ marginRight: "8px" }}></i>
                    {getWarehouseName(editingRoom.warehouseId)} (Admins only)
                  </div>
                ) : (
                  <>
                    <Dropdown
                      options={warehouseFormOptions}
                      value={roomForm.warehouseId}
                      onChange={(val) => setRoomForm({ ...roomForm, warehouseId: val })}
                      placeholder="Select a warehouse"
                      variant="outlined"
                    />
                    {editingRoom &&
                      roomForm.warehouseId &&
                      roomForm.warehouseId !==
                        (editingRoom.warehouseId?._id ||
                          editingRoom.warehouseId) && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "6px",
                            fontSize: "12px",
                            color: "#b45309",
                          }}
                        >
                          <i className="fa fa-exclamation-triangle" style={{ marginRight: "6px" }}></i>
                          Changing this will move the partition and every
                          tag inside it to the new warehouse.
                        </span>
                      )}
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowRoomModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingRoom ? updateRoom : createRoom}
              >
                <i className={`fa ${editingRoom ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingRoom ? "Update Partition" : "Create Partition"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag Assignment Modal */}
      {showTagAssignModal && selectedRoom && (
        <div className="modal-overlay" onClick={closeTagAssignModal}>
          <div className="modal-container" style={{ maxWidth: "800px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)", color: "#1E40AF" }}>
                <i className="fa fa-tags"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Manage Tags</span>
                <span className="modal-subtitle">
                  {selectedRoom.roomTitle} - Assign or remove RFID tags
                </span>
              </div>
              <div className="modal-close-btn" onClick={closeTagAssignModal}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper" style={{ maxHeight: "60vh" }}>
              {/* Room Info */}
              <div style={{
                display: "flex",
                gap: "16px",
                padding: "12px 16px",
                backgroundColor: "#f8fafc",
                borderRadius: "10px",
                marginBottom: "20px",
                alignItems: "center"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa fa-map-marker" style={{ color: "#4CAF50" }}></i>
                  <span style={{ fontSize: "14px", color: "#64748b" }}>{getLocationName(selectedRoom)}</span>
                </div>
                <i className="fa fa-chevron-right" style={{ color: "#cbd5e1", fontSize: "12px" }}></i>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa fa-building" style={{ color: "#FF9800" }}></i>
                  <span style={{ fontSize: "14px", color: "#64748b" }}>{getWarehouseName(selectedRoom.warehouseId)}</span>
                </div>
              </div>

              {/* Bulk Assign Bar */}
              {canManageRoomTags(selectedRoom) && selectedTagsForBulk.length > 0 && (
                <div className="bulk-assign-bar">
                  <span className="bulk-assign-count">
                    {selectedTagsForBulk.length} tag{selectedTagsForBulk.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="bulk-assign-actions">
                    <Button
                      value="Clear"
                      onClick={clearTagSelection}
                      variant="ghost"
                      size="sm"
                    />
                    <Button
                      value={`Assign ${selectedTagsForBulk.length} Tags`}
                      iconLeft="fa-plus"
                      onClick={bulkAssignTagsToRoom}
                      variant="primary"
                      size="sm"
                    />
                  </div>
                </div>
              )}

              {/* Bulk Remove Bar */}
              {canManageRoomTags(selectedRoom) && selectedTagsToRemove.length > 0 && (
                <div className="bulk-assign-bar" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
                  <span className="bulk-assign-count" style={{ color: "#991b1b" }}>
                    {selectedTagsToRemove.length} tag{selectedTagsToRemove.length !== 1 ? 's' : ''} selected for removal
                  </span>
                  <div className="bulk-assign-actions">
                    <Button
                      value="Clear"
                      onClick={clearRemoveSelection}
                      variant="ghost"
                      size="sm"
                    />
                    <Button
                      value={`Remove ${selectedTagsToRemove.length} Tags`}
                      iconLeft="fa-trash"
                      onClick={bulkRemoveTagsFromRoom}
                      variant="primary"
                      size="sm"
                      style={{ backgroundColor: "#dc2626" }}
                    />
                  </div>
                </div>
              )}

              {/* Assigned Tags */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                  <h4 style={{ margin: 0, color: "#0F172A", fontSize: "14px", fontWeight: "600" }}>
                    Assigned Tags ({roomTags.length})
                  </h4>
                  {canManageRoomTags(selectedRoom) && roomTags.length > 0 && (
                    <Button
                      value={selectedTagsToRemove.length === roomTags.length ? "Deselect All" : "Select All"}
                      onClick={selectAllAssignedTags}
                      variant="ghost"
                      size="sm"
                    />
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "200px", overflowY: "auto" }}>
                  {loadingTags ? (
                    <p style={{ color: "#94a3b8", fontSize: "14px", textAlign: "center", margin: "12px 0" }}>
                      <i className="fa fa-spinner fa-spin"></i> Loading tags...
                    </p>
                  ) : roomTags.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: "14px" }}>No tags assigned to this room</p>
                  ) : (
                    roomTags.map((tag) => {
                      const isRemoveSelected = selectedTagsToRemove.includes(tag.tid);
                      return (
                        <div
                          key={tag._id || tag.tid}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            backgroundColor: isRemoveSelected ? "#fee2e2" : "#f0fdf4",
                            borderRadius: "10px",
                            border: `1px solid ${isRemoveSelected ? "#fecaca" : "#bbf7d0"}`
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {canManageRoomTags(selectedRoom) && (
                              <input
                                type="checkbox"
                                checked={isRemoveSelected}
                                onChange={() => toggleRemoveSelection(tag.tid)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            <i className="fa fa-tag" style={{ color: "#22c55e" }}></i>
                            <div>
                              <span style={{ fontWeight: "600", color: "#0F172A", fontFamily: "monospace" }}>
                                {tag.tid}
                              </span>
                              {tag.name && (
                                <span style={{ marginLeft: "8px", color: "#64748b", fontSize: "12px" }}>
                                  ({tag.name})
                                </span>
                              )}
                            </div>
                          </div>
                          {canManageRoomTags(selectedRoom) && (
                            <button
                              onClick={() => removeTagFromRoom(tag.tid)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: "none",
                                backgroundColor: "#fee2e2",
                                color: "#dc2626",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight: "600"
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Available Tags */}
              {canManageRoomTags(selectedRoom) && (
                <div>
                  <div className="available-tags-header">
                    <h4 className="section-label">
                      Available Tags ({getAvailableTags().length})
                    </h4>
                    {getAvailableTags().length > 0 && (
                      <Button
                        value={selectedTagsForBulk.length === getAvailableTags().length ? "Deselect All" : "Select All"}
                        onClick={selectedTagsForBulk.length === getAvailableTags().length ? clearTagSelection : selectAllAvailableTags}
                        variant="ghost"
                        size="sm"
                      />
                    )}
                  </div>
                  <div className="available-tags-list">
                    {getAvailableTags().length === 0 ? (
                      <p className="empty-message">No unassigned tags available</p>
                    ) : (
                      getAvailableTags().map((tag) => {
                        const isSelected = selectedTagsForBulk.includes(tag.tid);
                        return (
                          <div
                            key={tag._id}
                            className={`tag-row ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleTagSelection(tag.tid)}
                          >
                            <div className="tag-row-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTagSelection(tag.tid)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="tag-row-info">
                              <i className="fa fa-tag"></i>
                              <span className="tag-tid">{tag.tid}</span>
                              {tag.name && (
                                <span className="tag-name">({tag.name})</span>
                              )}
                            </div>
                            <button
                              className="assign-single-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                assignTagToRoom(tag.tid);
                              }}
                            >
                              Assign
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn primary-btn" onClick={closeTagAssignModal}>
                <i className="fa fa-check"></i>
                <span>Done</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete Partition</span>
                <span className="modal-subtitle delete-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-cube"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteTarget.roomTitle}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    {(deleteTarget.tags?.length ?? 0) > 0
                      ? `This room contains ${deleteTarget.tags.length} tag(s). Do you want to delete them as well?`
                      : "This will remove the room and unassign any associated tags."}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="modal-footer-wrapper"
              style={(deleteTarget.tags?.length ?? 0) > 0 ? { flexDirection: "column", alignItems: "stretch" } : {}}
            >
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              {(deleteTarget.tags?.length ?? 0) > 0 ? (
                <>
                  <div
                    className="modal-action-btn delete-btn"
                    onClick={() => deleteRoom(false)}
                  >
                    <i className="fa fa-unlink"></i>
                    <span>Delete room only (unassign tags)</span>
                  </div>
                  <div
                    className="modal-action-btn delete-btn"
                    onClick={handleMoveTagsOnRoomDelete}
                  >
                    <i className="fa fa-arrow-right"></i>
                    <span>Move tags to another room</span>
                  </div>
                  <div
                    className="modal-action-btn delete-btn"
                    onClick={() => deleteRoom(true)}
                  >
                    <i className="fa fa-trash"></i>
                    <span>Delete room and tags</span>
                  </div>
                </>
              ) : (
                <div
                  className="modal-action-btn delete-btn"
                  onClick={() => deleteRoom(false)}
                >
                  <i className="fa fa-trash"></i>
                  <span>Delete Partition</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showMoveTagsModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowMoveTagsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle">
                <i className="fa fa-arrow-right"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Move Tags to Another Partition</span>
                <span className="modal-subtitle">
                  Select a target room for all tags from "{deleteTarget.roomTitle}"
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowMoveTagsModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Target Partition</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={[
                    { label: "Select a room", value: "" },
                    ...rooms
                      .filter(r => r.rid !== deleteTarget.rid)
                      .map(r => {
                        const warehouse = warehouses.find(w => (w._id || w.id) === (r.warehouseId?._id || r.warehouseId));
                        const location = locations.find(l => (l._id || l.id) === (r.locationId?._id || r.locationId));
                        const warehouseName = warehouse?.name || "Unknown warehouse";
                        const locationName = location?.name || "Unknown location";
                        return {
                          value: r.rid,
                          label: `${r.roomTitle} (RID ${r.rid}) — ${warehouseName} / ${locationName}`,
                        };
                      }),
                  ]}
                  value={selectedMoveRoomRid || ""}
                  onChange={(val) => setSelectedMoveRoomRid(val || null)}
                  placeholder="Select a room"
                  size="md"
                  width="100%"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => setShowMoveTagsModal(false)}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={confirmMoveTagsOnRoomDelete}
              >
                <i className="fa fa-check"></i>
                <span>Move Tags</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rooms;
